/**
 * CaptureFormModal — wraps CaptureFormRenderer in a modal dialog.
 *
 * Supports the capture target modes:
 *   - Deal target (win/loss): pass dealId + targetStageId. Submit sends
 *     { capture_form, deal, target_stage_id, values }. Invalidates ['deals'] and
 *     ['report-deals'] (refetchType:'none' to avoid CDC race).
 *   - Company target (change-in-demand): pass contactCompanyId. Submit sends
 *     { capture_form, contact_company, values }. Invalidates ['companyActivity',
 *     contactCompanyId] so the timeline refreshes with the new capture.
 *   - Activity target (activity-gated): pass activityId. Submit sends
 *     { capture_form, activity, values }.
 *
 * Props:
 *   form              — the CaptureForm supplying form fields and metadata.
 *   dealId            — (deal target) the deal being moved.
 *   targetStageId     — (deal target) the stage the deal is moving into.
 *   contactCompanyId  — (company target) the company the capture is attached to.
 *   activityId        — (activity target) the activity the capture is filled on.
 *   isOpen            — controls modal visibility.
 *   onClose           — called when the modal is dismissed without a successful submit.
 *   onSuccess         — optional callback fired after a successful submit (before onClose).
 *
 * Uses the react-bootstrap Modal from components/UI/Modal (not FbModal) because
 * CaptureFormRenderer owns its own <form> element, and nesting two <form>s is
 * invalid HTML. The react-bootstrap variant renders the body as a plain div.
 */
import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Modal } from 'components/UI/Modal'
import { Alert } from 'components/FbUI/Alert'
import { useSubmitCapture } from 'services/captureForms'
import type { CaptureForm, CaptureSubmitPayload } from 'models/captureForms'
import { CaptureFormRenderer } from './CaptureFormRenderer'
import type { CaptureSubmitValues } from './CaptureFormRenderer'

export interface CaptureFormModalProps {
  form: CaptureForm
  /** Deal target: the deal being moved to targetStageId. */
  dealId?: number
  /** Deal target: the stage the deal is moving into (sent to submit endpoint). */
  targetStageId?: number
  /** Company target: the contact_company this capture is attached to. */
  contactCompanyId?: number
  /** Activity target: the activity this capture is filled on. */
  activityId?: number
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
}

export function CaptureFormModal({
  form,
  dealId,
  targetStageId,
  contactCompanyId,
  activityId,
  isOpen,
  onClose,
  onSuccess,
}: CaptureFormModalProps) {
  const queryClient = useQueryClient()
  const [submitError, setSubmitError] = useState<string | null>(null)

  const { mutate, isPending } = useSubmitCapture()

  function handleSubmit({ values, lines }: CaptureSubmitValues) {
    setSubmitError(null)

    const payload: CaptureSubmitPayload =
      contactCompanyId != null
        ? {
            capture_form: form.id,
            contact_company: contactCompanyId,
            values,
            lines,
          }
        : activityId != null
          ? {
              capture_form: form.id,
              activity: activityId,
              values,
              lines,
            }
          : {
              capture_form: form.id,
              deal: dealId,
              target_stage_id: targetStageId,
              values,
              lines,
            }

    mutate(payload, {
      onSuccess: () => {
        if (contactCompanyId != null) {
          // Invalidate the company activity timeline so the new capture appears.
          queryClient.invalidateQueries({
            queryKey: ['companyActivity', contactCompanyId],
          })
        } else {
          // Invalidate deal caches so the board and detail views reflect the move.
          // refetchType:'none' avoids the CDC read-model race (mirrors
          // DealsKanbanBoard.handleEditModalClose and the onSuccess handler there):
          // the move is already applied server-side, so mark stale and let the
          // next mount/focus refetch once CDC has caught up.
          queryClient.invalidateQueries({
            queryKey: ['deals'],
            refetchType: 'none',
          })
          queryClient.invalidateQueries({
            queryKey: ['report-deals'],
            refetchType: 'none',
          })
        }
        onSuccess?.()
        onClose()
      },
      onError: (err: unknown) => {
        const message =
          err instanceof Error ? err.message : 'Failed to submit form'
        setSubmitError(message)
      },
    })
  }

  return (
    <Modal
      show={isOpen}
      onHide={isPending ? undefined : onClose}
      backdrop={isPending ? 'static' : true}
      centered
    >
      <Modal.Header closeButton={!isPending}>
        <Modal.Title>{form.label}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {submitError && (
          <Alert color="danger" description={submitError} className="mb-4" />
        )}
        <CaptureFormRenderer
          form={form}
          onSubmit={handleSubmit}
          submitting={isPending}
        />
      </Modal.Body>
    </Modal>
  )
}
